'use client';

import { useEffect } from 'react';
import { LocaleCode } from '@/i18n/locales';
import { useSearchParams } from 'next/navigation';
import { buildSiteInformation } from '@/features/games-app-bridge/internal/util/build-site-information';
import { useUser } from '@/features/auth/shared/hooks/use-user';
import { useCurrency } from '@/features/shared/hooks/use-currency';
import { useCountryISO } from '@/features/shared/hooks/use-country-iso';

interface LibrarySiteSettingsSyncProps {
  locale: LocaleCode;
}

/**
 * This component is on the /libary-loader page.
 * It is loaded in an iframe by our games-app-ui.
 * It transferes information about the user and the website in general to the games-app-ui.
 * It especially kicks of the loading process on the library page by generating an event.
 * The games-app uses this infomation to:
 *  - have the navigation links in its own UI point to the correct pages
 *  - get a JWT from the website to call backendservice in AWS (and GCP)
 *  - get the logged in user information
 *
 * @component
 */
export const LibrarySiteSettingsSync = ({ locale }: LibrarySiteSettingsSyncProps) => {
  const { loading, authenticatedUser } = useUser();
  const currency = useCurrency();
  const countryISO = useCountryISO();
  const searchParams = useSearchParams();

  useEffect(() => {
    if (!loading) {
      const siteInformation = buildSiteInformation(authenticatedUser, locale, currency, countryISO);
      const eventType = searchParams?.get('eventType') ?? 'wt-extra';
      const evt = new CustomEvent(eventType, {
        detail: siteInformation,
      });
      if (window.parent) window.parent.dispatchEvent(evt);
    }
    // Taken from the old bigcommerce-stencil although I'm not sure this is really needed.
    // I don't think there are circumstances where this can change while the user is on the library page.
    const id = window.setInterval(() => {
      window.location.reload();
    }, 300_000);

    return () => {
      window.clearInterval(id);
    };
  }, [loading, authenticatedUser, locale, currency, countryISO, searchParams]);

  return null;
};
